import { useState } from 'react';
import {
  downloadExpensesByTaxCsv,
  downloadProjectProfitabilityCsv,
  downloadRevenueCsv,
} from '../api/queries/financials';
import { DateInput } from '../components/DateInput';
import { toast } from '../components/Toasts';
import { can } from '../auth/store';
import { ApiError } from '../api/client';
import { Button, Card, FormField } from '../components/ui';

interface ReportDef {
  key: string;
  title: string;
  description: string;
  perm: string;
  download: (since: number | null, until: number | null) => Promise<void>;
}

const REPORTS: ReportDef[] = [
  {
    key: 'expenses-by-tax',
    title: 'Expenses by tax category',
    description: 'Schedule-C worksheet: totals per tax category (amount + tax split).',
    perm: 'expense.read',
    download: downloadExpensesByTaxCsv,
  },
  {
    key: 'revenue',
    title: 'Revenue',
    description: 'Cleared income per client over the range, with outstanding invoice totals.',
    perm: 'payment.read',
    download: downloadRevenueCsv,
  },
  {
    key: 'project-profitability',
    title: 'Project profitability',
    description: 'Per project: income in, expenses and contractor payouts out, net.',
    perm: 'project.read',
    download: downloadProjectProfitabilityCsv,
  },
];

export function FinancialReportsPage(): JSX.Element {
  const yearStart = new Date(new Date().getFullYear(), 0, 1).getTime();
  const [since, setSince] = useState<number | null>(yearStart);
  const [until, setUntil] = useState<number | null>(Date.now());
  const [busyKey, setBusyKey] = useState<string | null>(null);

  const visible = REPORTS.filter((r) => can(r.perm));

  const run = async (r: ReportDef) => {
    setBusyKey(r.key);
    try {
      await r.download(since, until);
    } catch (err) {
      toast.danger(err instanceof ApiError ? err.message : 'Export failed.');
    } finally {
      setBusyKey(null);
    }
  };

  return (
    <div className="page financial-reports-page">
      <header className="page-header">
        <div>
          <h1>Financial Reports</h1>
          <p className="page-sub">CSV exports over a date range (defaults to year-to-date).</p>
        </div>
      </header>

      <div className="filters-bar">
        <FormField label="From">
          <DateInput value={since} onChange={setSince} />
        </FormField>
        <FormField label="To">
          <DateInput value={until} onChange={setUntil} />
        </FormField>
      </div>

      <div className="settings-grid">
        {visible.map((r) => (
          <Card key={r.key}>
            <Card.Header>
              <Card.Title>{r.title}</Card.Title>
            </Card.Header>
            <Card.Body>
              <p className="settings-hint">{r.description}</p>
              <div className="settings-actions">
                <Button
                  variant="primary"
                  disabled={busyKey !== null}
                  onClick={() => void run(r)}
                >
                  {busyKey === r.key ? 'Exporting…' : 'Download CSV'}
                </Button>
              </div>
            </Card.Body>
          </Card>
        ))}
        {visible.length === 0 && (
          <p className="muted">You don't have permission to run any financial reports.</p>
        )}
      </div>
    </div>
  );
}
